import { createInstance, type i18n as I18nType } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Dict } from '../i18n/deepMerge';

/**
 * Build a fresh, synchronously-initialised i18next instance for one language.
 *
 * `translation` is that language's dictionary, already merged onto English
 * (src/i18n/loadTranslation.ts). It is passed in rather than imported here:
 * importing the locale files in this module put all six languages into every
 * page's JavaScript. Only the page's own language is loaded now; others are
 * added on demand when someone switches language (LanguageContext).
 *
 * The English base inside every merged dictionary means each key exists, so
 * the instance never needs English as a separate fallback bundle.
 *
 * One consequence for plurals: a language must define every plural form its
 * own rules ask for. Polish needs _one, _few, _many and _other; English only
 * has _one and _other, so a Polish key missing _few renders as a raw key
 * instead of falling back to English text as it used to.
 */
export function createI18nInstance(lng: string, translation: Dict): I18nType {
  const instance = createInstance();
  instance.use(initReactI18next).init({
    resources: { [lng]: { translation } },
    lng,
    fallbackLng: lng,
    supportedLngs: ['en', 'fi', 'sv', 'da', 'de', 'pl'],
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    initImmediate: false, // synchronous init → ready before first render
    react: {
      useSuspense: false,
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
    },
  });
  return instance;
}
